// PayRequestPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Paper, Avatar, Stack, Chip, Skeleton, CircularProgress,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  HourglassBottom as ExpiredIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { alpha, keyframes } from "@mui/material/styles";
import client from "../../../API";
// NOTE: confirm exact on-disk casing of this file (PaymentCTA.jsx vs paymentCTA.jsx).
// macOS/Windows are case-insensitive so a mismatch works locally but FAILS on
// Vercel's Linux build with a module-not-found error. Fix this import to match
// the real filename exactly before deploying.
import PaymentCTA from "../../Dashboard/invoicing/invoicecomps/paymentCTA";

// ─── Brand tokens — same family as InternalTransfer.jsx "Request" mode ───────
const BLITZ = {
  sky: "#0ea5e9", skyDark: "#0284c7", skyDeep: "#075985", skyLight: "#7dd3fc",
  ink: "#0f172a",
};

const fadeUp = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / 86400000);
}

// ─── Shared page shell — keeps every state visually consistent ───────────────
function PageShell({ children, narrow = false }) {
  return (
    <Box sx={{
      minHeight: "100vh", position: "relative", overflow: "hidden",
      background: `linear-gradient(160deg, ${BLITZ.skyDeep} 0%, ${BLITZ.sky} 55%, ${alpha(BLITZ.skyLight, 0.9)} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      py: { xs: 4, md: 8 }, px: 2,
    }}>
      {/* faint grid texture, same trick as InternalTransfer's hero */}
      <Box sx={{
        position: "absolute", inset: 0, opacity: 0.06, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />
      <Box sx={{
        position: "absolute", top: -120, right: -100, width: 320, height: 320, borderRadius: "50%",
        background: `radial-gradient(circle, ${alpha("#fff", 0.18)} 0%, transparent 70%)`, pointerEvents: "none",
      }} />
      <Box sx={{ width: "100%", maxWidth: narrow ? 420 : 480, position: "relative", zIndex: 1, animation: `${fadeIn} 0.3s ease` }}>
        {children}
      </Box>
    </Box>
  );
}

function BrandMark() {
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha("#fff", 0.18),
        display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)",
        border: `1px solid ${alpha("#fff", 0.3)}`,
      }}>
        <WalletIcon sx={{ fontSize: 16, color: "white" }} />
      </Box>
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: 800, letterSpacing: "0.02em" }}>
        Blitz Pay
      </Typography>
    </Stack>
  );
}

// ─── Loading state ────────────────────────────────────────────────────────────
function LoadingState() {
  return (
    <PageShell narrow>
      <BrandMark />
      <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <CircularProgress size={32} thickness={4} sx={{ color: BLITZ.sky, mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Loading payment request…
        </Typography>
        <Stack spacing={1} sx={{ mt: 3 }}>
          <Skeleton variant="rounded" height={14} width="60%" sx={{ mx: "auto" }} />
          <Skeleton variant="rounded" height={36} width="40%" sx={{ mx: "auto" }} />
        </Stack>
      </Paper>
    </PageShell>
  );
}

// ─── Problem state — distinct copy/icon per cause ─────────────────────────────
function ProblemState({ kind, message, amount }) {
  const variants = {
    paid: {
      icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
      iconBg: "#dcfce7", iconColor: "#16a34a",
      title: "Already paid",
      body: amount
        ? `This ${fmt(amount)} request has already been settled. No further action is needed.`
        : "This request has already been settled. No further action is needed.",
    },
    expired: {
      icon: <ExpiredIcon sx={{ fontSize: 40 }} />,
      iconBg: alpha(BLITZ.sky, 0.12), iconColor: BLITZ.skyDark,
      title: "This link has expired",
      body: "Ask the sender to issue a new payment request — this one is no longer valid.",
    },
    notfound: {
      icon: <ErrorIcon sx={{ fontSize: 40 }} />,
      iconBg: "#fef2f2", iconColor: "#dc2626",
      title: "Request not found",
      body: "We couldn't find a payment request at this link. Double-check the URL you were sent.",
    },
    error: {
      icon: <ErrorIcon sx={{ fontSize: 40 }} />,
      iconBg: "#fef2f2", iconColor: "#dc2626",
      title: "Something went wrong",
      body: message || "Please try again, or contact the person who sent you this link.",
    },
  };
  const v = variants[kind] || variants.error;

  return (
    <PageShell narrow>
      <BrandMark />
      <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "50%", bgcolor: v.iconBg, color: v.iconColor,
          display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5,
        }}>
          {v.icon}
        </Box>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>{v.title}</Typography>
        <Typography variant="body2" color="text.secondary">{v.body}</Typography>
      </Paper>
    </PageShell>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function PayRequestPage() {
  const { token } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null); // { kind, message }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProblem(null);

    client.balanceLedger
      .getRequestByToken(token)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const apiMsg = err?.response?.data?.error;
        const apiStatus = err?.response?.data?.status;
        if (apiStatus === "paid" || (status === 410 && /paid/i.test(apiMsg || ""))) {
          setProblem({ kind: "paid", message: apiMsg });
        } else if (apiStatus === "expired" || status === 410) {
          setProblem({ kind: "expired", message: apiMsg });
        } else if (status === 404) {
          setProblem({ kind: "notfound", message: apiMsg });
        } else {
          setProblem({ kind: "error", message: apiMsg });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [token]);

  if (loading) return <LoadingState />;
  if (problem) return <ProblemState kind={problem.kind} message={problem.message} amount={data?.amount} />;
  if (!data) return <ProblemState kind="error" />;

  const dLeft = daysUntil(data.due_date);
  const dueChip = dLeft == null ? null : dLeft < 0
    ? { label: "Overdue", color: "#dc2626", bg: "#fef2f2" }
    : dLeft <= 2
    ? { label: `Due in ${dLeft}d`, color: "#d97706", bg: "#fffbeb" }
    : { label: `Due ${new Date(data.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`, color: BLITZ.skyDark, bg: alpha(BLITZ.sky, 0.1) };

  // Adapt /pay-request/<token> response into the invoice shape PaymentCTA expects.
  // The GET response has no `unique_code` field — the token in the URL *is* the code.
  // Also note: this object has no creator_id/user_id (the requester, not a
  // payment recipient, is the payee here) — that's WHY we pass forcePublicOnly
  // below instead of letting PaymentCTA infer auth state on its own.
  const invoiceForCTA = {
    id:           data.invoice_id,
    amount_due:   data.amount,
    unique_code:  token,
    po_number:    data.po_number,
    client_notes: data.description,
    status:       data.status,
  };

  return (
    <PageShell>
      <BrandMark />

      {/* ── Request summary hero ── */}
      <Paper elevation={0} sx={{
        borderRadius: 4, p: { xs: 3, md: 3.5 }, mb: 2, position: "relative", overflow: "hidden",
        animation: `${fadeUp} 0.25s ease`,
      }}>
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4,
          background: `linear-gradient(90deg, ${BLITZ.skyDeep}, ${BLITZ.sky})` }} />

        <Stack direction="row" alignItems="flex-start" spacing={1.75} sx={{ mb: 2 }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%",
              background: `linear-gradient(135deg, ${BLITZ.sky}, ${alpha(BLITZ.sky, 0.5)})` }} />
            <Avatar sx={{
              width: 46, height: 46, position: "relative", border: "3px solid white",
              bgcolor: alpha(BLITZ.sky, 0.12), color: BLITZ.skyDark, fontWeight: 800,
            }}>
              {(data.requester_name || "?")[0].toUpperCase()}
            </Avatar>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
              Payment requested by
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {data.requester_name}
            </Typography>
          </Box>
          {dueChip && (
            <Chip
              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
              label={dueChip.label}
              size="small"
              sx={{ height: 24, fontWeight: 700, fontSize: "0.7rem", bgcolor: dueChip.bg, color: dueChip.color,
                "& .MuiChip-icon": { color: dueChip.color } }}
            />
          )}
        </Stack>

        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: "-0.03em", color: BLITZ.ink, lineHeight: 1, mb: 2 }}>
          {fmt(data.amount)}
        </Typography>

        {data.description && (
          <Box sx={{ bgcolor: alpha(BLITZ.sky, 0.05), border: `1px solid ${alpha(BLITZ.sky, 0.15)}`,
            borderRadius: 2.5, p: 1.75, mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: BLITZ.skyDark, textTransform: "uppercase",
              letterSpacing: "0.07em", fontSize: "0.62rem", display: "block", mb: 0.4 }}>
              What this is for
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary" }}>{data.description}</Typography>
          </Box>
        )}

        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ opacity: 0.6 }}>
          <ReceiptIcon sx={{ fontSize: 13 }} />
          <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
            {data.po_number}
          </Typography>
        </Stack>
      </Paper>

      {/* ── Payment widget ── */}
      <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 3.5 }, animation: `${fadeUp} 0.3s ease` }}>
        <PaymentCTA
          invoice={invoiceForCTA}
          invoiceAmount={data.amount}
          variant="regular"
          forcePublicOnly
        />
      </Paper>

      {/* ── Trust footer ── */}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 2.5, opacity: 0.85 }}>
        <LockIcon sx={{ fontSize: 13, color: "white" }} />
        <Typography variant="caption" sx={{ color: "white", fontSize: 11 }}>
          Secured by Blitz · Stripe · PayPal
        </Typography>
      </Stack>
    </PageShell>
  );
}